import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TabPanelInner } from 'components/Tabs';
import { Alert, Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { ClearContentTypes } from 'features/application/types';

import { closeModal, openModal } from 'features/modal/modalSlice';
import { selectFeatureFlags } from 'features/feature-set/featuresSlice';

type FilteredClearContentTypes = Omit<ClearContentTypes, 'quizResults'>;

const contentTypeOptions: Array<{ key: keyof FilteredClearContentTypes; label: string }> = [
  { key: 'publicChat', label: 'clear_content.content_types.chat_messages' },
  { key: 'roomLibrary', label: 'clear_content.content_types.public_room_files' },
  { key: 'sharedFiles', label: 'clear_content.content_types.shared_files' },
  { key: 'whiteboards', label: 'clear_content.content_types.whiteboards' },
  { key: 'sharedNotes', label: 'clear_content.content_types.shared_notes' },
  { key: 'qa', label: 'clear_content.content_types.qa_entries' },
  { key: 'pollResults', label: 'clear_content.content_types.poll_results' },
];

// Map clear content types to their corresponding feature flags
const contentTypeFeatureMapping: Record<keyof FilteredClearContentTypes, string> = {
  publicChat: 'generalChat',
  roomLibrary: 'contentLibrary',
  sharedFiles: 'contentLibrary',
  whiteboards: 'whiteboard',
  sharedNotes: 'sharedNotes',
  qa: 'qa',
  pollResults: 'polls',
};

const ClearContentTab = () => {
  const { t } = useTranslation('settings');

  const dispatch = useAppDispatch();
  const featureFlags = useAppSelector(selectFeatureFlags);

  const isContentTypeFeatureEnabled = useCallback(
    (contentType: keyof FilteredClearContentTypes): boolean => {
      const featureFlag = contentTypeFeatureMapping[contentType];
      if (!featureFlag) {
        return true;
      }

      return featureFlags[featureFlag as keyof typeof featureFlags] || false;
    },
    [featureFlags]
  );

  const [selectedContentTypes, setSelectedContent] = useState<FilteredClearContentTypes>({
    publicChat: false,
    roomLibrary: false,
    sharedFiles: false,
    whiteboards: false,
    sharedNotes: false,
    qa: false,
    pollResults: false,
  });

  useEffect(() => {
    setSelectedContent((prevContent) => {
      const updatedContent = { ...prevContent };
      let hasChanges = false;

      Object.keys(prevContent).forEach((key) => {
        const contentType = key as keyof FilteredClearContentTypes;
        const isFeatureDisabled = !isContentTypeFeatureEnabled(contentType);

        if (prevContent[contentType] && isFeatureDisabled) {
          updatedContent[contentType] = false;
          hasChanges = true;
        }
      });

      return hasChanges ? updatedContent : prevContent;
    });
  }, [featureFlags, isContentTypeFeatureEnabled]);

  const handleCheckboxChange =
    (contentType: keyof FilteredClearContentTypes) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!isContentTypeFeatureEnabled(contentType)) {
        return;
      }

      setSelectedContent((prev) => ({
        ...prev,
        [contentType]: event.target.checked,
      }));
    };

  const handleClearContent = () => {
    dispatch(closeModal('roomSettings'));
    dispatch(openModal('clearContentConfirmation', { selectedContentTypes }));
  };

  const hasSelectedContent = Object.values(selectedContentTypes).some((value) => value);

  return (
    <TabPanelInner>
      <Box sx={{ mb: 3 }}>
        <Typography>{t('clear_content.description')}</Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        {contentTypeOptions.map(({ key, label }) => {
          const isDisabled = !isContentTypeFeatureEnabled(key);

          return (
            <div key={key}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedContentTypes[key]}
                    onChange={handleCheckboxChange(key)}
                    name={key}
                    disabled={isDisabled}
                  />
                }
                label={t(label)}
              />
            </div>
          );
        })}
      </Box>

      <Box
        sx={{
          mb: 2,
        }}
      >
        <Alert severity="error">{t('clear_content.disclaimer_warming')}</Alert>
      </Box>

      <Box sx={{ mb: 6 }}>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          {t('clear_content.personal_files_help_text')}
        </Typography>
      </Box>

      <Box>
        <Button
          variant="contained"
          color="error"
          onClick={handleClearContent}
          disabled={!hasSelectedContent}
        >
          {t('clear_content.btn_clear_label')}
        </Button>
      </Box>
    </TabPanelInner>
  );
};

export default ClearContentTab;
